// GoHighLevel Affiliate Manager tracking (client-side scripts only).
// Injected per-firm: the location ID comes from the white-label config, the
// cookie domain from the portal's own hostname — never shared across firms.

export interface GhlAffiliateConfig {
  enabled?: boolean | null;
  locationId?: string | null;
}

const GHL_SCRIPT_SRC = "https://link.msgsndr.com/js/am.js";
const GHL_BACKEND_URL = "https://backend.leadconnectorhq.com";
const AM_ID_PARAM = "am_id";

// Captured at module-eval time: auth-guard redirects replace the URL with
// returnTo=pathname only, dropping ?am_id= before am.js can read it.
let capturedAmId: string | null = null;

export const captureAffiliateParam = (search?: string): string | null => {
  try {
    capturedAmId = new URLSearchParams(
      search ?? window.location.search
    ).get(AM_ID_PARAM);
  } catch {
    capturedAmId = null;
  }
  return capturedAmId;
};

if (typeof window !== "undefined") {
  captureAffiliateParam();
}

const restoreAmIdToUrl = (): void => {
  try {
    if (!capturedAmId) return;
    const url = new URL(window.location.href);
    if (!url.searchParams.get(AM_ID_PARAM)) {
      url.searchParams.set(AM_ID_PARAM, capturedAmId);
      window.history.replaceState(window.history.state, "", url.toString());
    }
  } catch {
    // never block app boot
  }
};

// Leading-dot form of the portal's own host; leading-dot cookies are invalid
// on localhost/IPs so those get the plain hostname.
export const deriveCookieDomain = (hostname: string): string => {
  const isIpV4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
  if (hostname === "localhost" || isIpV4 || !hostname.includes(".")) {
    return hostname;
  }
  return `.${hostname}`;
};

export const splitFullName = (
  fullName: string
): { firstName: string; lastName: string } => {
  const parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
  return { firstName: parts[0] || "", lastName: parts.slice(1).join(" ") };
};

class GhlAffiliate {
  initialized = false;

  initialize(config: GhlAffiliateConfig) {
    if (this.initialized) {
      return;
    }
    // No third-party JS for firms that haven't opted in
    if (!config?.enabled || !config?.locationId) {
      return;
    }
    if (typeof document === "undefined") {
      return;
    }
    this.initialized = true;

    const locationId = config.locationId;
    const cookieDomain = deriveCookieDomain(window.location.hostname);

    // Router redirects during app boot (guarded routes → auth entry) strip
    // ?am_id= from the URL. Restore it now AND again right before init():
    // the script loads async, so a boot redirect can land in between and
    // wipe the first restore. GHL reads the URL when init() runs.
    restoreAmIdToUrl();

    const script = document.createElement("script");
    script.src = GHL_SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      try {
        restoreAmIdToUrl();
        (window as any).affiliateManager?.init(
          locationId,
          GHL_BACKEND_URL,
          cookieDomain
        );
      } catch (err) {
        console.error("GHL affiliate init failed", err);
      }
    };
    script.onerror = () => {
      console.error("GHL affiliate script failed to load");
    };
    document.head.appendChild(script);
  }

  // Fire-and-forget: must never block or fail signup.
  // eslint-disable-next-line class-methods-use-this
  trackLead(lead: { firstName: string; lastName: string; email: string }) {
    try {
      const am = (window as any).affiliateManager;
      if (!am || typeof am.trackLead !== "function") {
        return; // script not rendered (firm not enabled) or blocked
      }
      am.trackLead(lead, () => {});
    } catch (err) {
      console.error("GHL trackLead failed", err);
    }
  }

  // Test-only: reset singleton state between test cases.
  resetForTests() {
    this.initialized = false;
  }
}

// Singleton
export const ghlAffiliate = new GhlAffiliate();
